/**
 * A spreadsheet of companies, onto the board — minus the ones we already sell to.
 *
 * Three steps, because the middle one is the whole point. Four hundred rows
 * off a bought or exported list will be half people who have already
 * registered, and putting those on the pipeline asks the team to chase their
 * own customers: the row looks exactly like a real enquiry and nothing on it
 * says otherwise. So the list is matched against Clients before anything is
 * written, and what will be left out is shown — with the names — before the
 * button that writes it.
 *
 * Matched on the email address and nothing else. A company name is typed
 * differently every time somebody types it and a phone number is written five
 * ways; the address is the one field that is either the same string on both
 * sides or is not the same person. Rows with no address cannot be checked at
 * all, so they are counted separately and it is a decision whether they come.
 *
 * The file is read on the server. A spreadsheet reader is a megabyte of
 * JavaScript that every visitor would download to use once, and there is
 * already an endpoint that does it for the marketing import.
 */
import { useMemo, useRef, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import { fileToBase64 } from '../../lib/format.js';
import { Spinner } from '../Primitives.jsx';
import { sortImport, leadDetails, boardRows } from '../../domain/crm.js';
import Sheet, { Group, Field } from '../Sheet.jsx';

const STEPS = ['Upload', 'Check the list', 'Confirm'];

/* The three columns this screen wants, and the spellings a real file uses.
   Matched on letters alone, so "Mobile No.", "mobile_no" and "MOBILE NO" are
   one thing. */
const FIELDS = [
  ['company', 'Company', ['company', 'companyname', 'name', 'organisation', 'organization', 'business']],
  ['email', 'Email', ['email', 'mail', 'mailid', 'emailid', 'emailaddress', 'e']],
  ['phone', 'Mobile', ['phone', 'mobile', 'mobileno', 'mobilenumber', 'phoneno', 'contact', 'number']],
  /* The label the row travels under — see segmentOf. Optional, and the
     column is usually headed "Source" on a bought list even though it is
     not the source this app means, so that spelling is accepted too. */
  ['segment', 'Segment', ['segment', 'source', 'tag', 'category', 'type', 'clienttype', 'kind', 'label']]
];

const norm = h => String(h || '').toLowerCase().replace(/[^a-z]/g, '');

export default function ImportLeads({ into = 'pipeline', onClose }) {
  const { crm, rows, importLeads, importContacts, toast } = useApp();
  /* One screen, two destinations. The file, the mapping and — above all —
     the check against Clients are identical; only the table the rows land in
     differs, and writing the whole thing twice is how the two would drift. */
  const toContacts = into === 'contacts';

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [grid, setGrid] = useState([]);          // every row, header included
  const [map, setMap] = useState({});            // field -> column index
  const [takeBlank, setTakeBlank] = useState(true);
  const fileRef = useRef(null);

  const offline = isOffline();

  /* Read the file, then guess the columns. The guess is shown as three
     dropdowns rather than applied silently: a file whose first column is the
     phone number is not unusual, and a silent guess would import four hundred
     rows under the wrong heading. */
  const pick = async file => {
    if (!file) return;
    setBusy(true);
    try {
      const out = await api.marketing.sheet({
        name: file.name,
        data: await fileToBase64(file)
      });
      const parsed = out.rows || [];
      if (!parsed.length) { toast('That file has no rows'); return; }
      if (out.truncated) toast(`Only the first ${parsed.length} rows were read`);

      const head = parsed[0].map(norm);
      const guess = {};
      FIELDS.forEach(([key, , names]) => {
        const at = head.findIndex(h => h && names.includes(h));
        if (at > -1) guess[key] = at;
      });

      setGrid(parsed);
      setMap(guess);
      setStep(1);
    } catch (e) {
      toast(e.message || 'That file could not be read');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /* The header row is dropped: it is the row the columns were named from. */
  const body = grid.length > 1 ? grid.slice(1) : [];

  const listed = useMemo(() => body.map(r => ({
    company: map.company != null ? r[map.company] : '',
    email: map.email != null ? r[map.email] : '',
    phone: map.phone != null ? r[map.phone] : '',
    segment: map.segment != null ? String(r[map.segment] || '').trim().toUpperCase() : ''
  })), [body, map]);

  /*
   * Everybody we already sell to, and everybody already on the board.
   *
   * Clients come from the connected app and are the authority on who has
   * registered. The board is checked as well, because importing the same
   * list twice is the second thing anybody does with an import.
   */
  const split = useMemo(() => {
    if (step < 1) return null;
    const clientMails = (rows || []).map(c => c.email);
    const boardMails = boardRows(crm).map(d => leadDetails(crm, d).email);
    return sortImport(listed, clientMails, boardMails);
  }, [step, listed, rows, crm]);

  const coming = split
    ? [...split.fresh, ...(takeBlank ? split.noEmail : [])]
    : [];

  const go = () => {
    const n = toContacts ? importContacts(coming) : importLeads(coming);
    onClose();
    toast(n
      ? (toContacts
        ? `${n} contact${n === 1 ? '' : 's'} added`
        : `${n} lead${n === 1 ? '' : 's'} on the board`)
      : 'Nothing to import');
  };

  /* ------------------------------------------------------------- render */

  const rail = (
    <div className="imp-rail">
      {STEPS.map((label, i) => (
        <span key={label} className={`imp-step${i === step ? ' on' : ''}${i < step ? ' done' : ''}`}>
          <b>{i < step ? <Icon name="check" /> : i + 1}</b>{label}
        </span>
      ))}
    </div>
  );

  if (offline) {
    return (
      <Sheet icon="up" title="Import" onClose={onClose} closeLabel="Close">
        <Field label="" wide>
          <p className="imp-note">
            The file is read on the server, and there is no server offline.
          </p>
        </Field>
      </Sheet>
    );
  }

  return (
    <Sheet
      icon="up"
      tone="g-blue"
      wide
      title={toContacts ? 'Import contacts' : 'Import leads'}
      onClose={onClose}
      onSave={step === 2 ? go : undefined}
      saveLabel={`Import ${coming.length}`}
      closeLabel={step === 2 ? 'Back out' : 'Cancel'}
      hero={rail}
      footerLeft={step === 1 ? (
        <button
          type="button"
          className="btn primary"
          disabled={map.email == null && map.company == null}
          onClick={() => setStep(2)}
        >
          Check it<Icon name="chev" />
        </button>
      ) : null}
    >
      {step === 0 ? (
        <>
          <Group>The file</Group>
          <Field label="" wide>
            <label className={`imp-drop${busy ? ' busy' : ''}`}>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => pick(e.target.files?.[0])}
              />
              {busy ? <Spinner /> : <Icon name="sheet" />}
              <b>{busy ? 'Reading…' : 'Choose a spreadsheet'}</b>
              <span>.xlsx, .xls or .csv — company, email, mobile number, and a segment if the list has one</span>
            </label>
          </Field>
          <Field label="" wide>
            <p className="imp-note">
              <Icon name="alert" />
              Anybody on the list who has already registered stays off the
              board: they are a client, and a client is not a lead. They are
              matched by email address, and you will see who they were before
              anything is written.
            </p>
          </Field>
        </>
      ) : null}

      {step === 1 ? (
        <>
          <Group>{`Which column is which — ${body.length} rows`}</Group>
          {FIELDS.map(([key, label]) => (
            <Field key={key} label={label}>
              <select
                value={map[key] ?? ''}
                onChange={e => setMap(m => ({
                  ...m,
                  [key]: e.target.value === '' ? undefined : Number(e.target.value)
                }))}
              >
                <option value="">— not in this file —</option>
                {(grid[0] || []).map((h, i) => (
                  <option key={i} value={i}>{String(h || `Column ${i + 1}`)}</option>
                ))}
              </select>
            </Field>
          ))}

          <Field label="" wide>
            <div className="imp-prev">
              <table>
                <thead>
                  <tr><th>Company</th><th>Email</th><th>Mobile</th><th>Segment</th></tr>
                </thead>
                <tbody>
                  {listed.slice(0, 6).map((r, i) => (
                    <tr key={i}>
                      <td>{r.company || <i>—</i>}</td>
                      <td>{r.email || <i>—</i>}</td>
                      <td>{r.phone || <i>—</i>}</td>
                      <td>{r.segment || <i>—</i>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {body.length > 6 ? <small>and {body.length - 6} more</small> : null}
            </div>
          </Field>
        </>
      ) : null}

      {step === 2 && split ? (
        <>
          <Group>What will happen</Group>
          <Field label="" wide>
            <div className="imp-tiles">
              <div className="imp-tile go">
                <b>{coming.length}</b><span>{toContacts ? 'into Contacts' : 'onto the board'}</span>
              </div>
              <div className="imp-tile skip">
                <b>{split.clients.length}</b><span>already clients</span>
              </div>
              <div className="imp-tile skip">
                <b>{split.onBoard.length}</b><span>already on the board</span>
              </div>
              <div className="imp-tile skip">
                <b>{split.repeats.length}</b><span>repeated in the file</span>
              </div>
            </div>
          </Field>

          {/* Named, not counted. "217 skipped" is a number somebody has to
              take on trust; the list is what makes the rule checkable. */}
          {split.clients.length ? (
            <Field label="" wide>
              <details className="imp-why" open>
                <summary>
                  <Icon name="users" />
                  {`${split.clients.length} are already registered — they stay off the board`}
                </summary>
                <ul>
                  {split.clients.slice(0, 40).map(r => (
                    <li key={`c${r.line}`}>
                      <b>{r.company || r.email}</b><span>{r.email}</span>
                    </li>
                  ))}
                </ul>
                {split.clients.length > 40
                  ? <small>and {split.clients.length - 40} more</small> : null}
              </details>
            </Field>
          ) : null}

          {split.onBoard.length ? (
            <Field label="" wide>
              <details className="imp-why">
                <summary>
                  <Icon name="zap" />
                  {`${split.onBoard.length} are on the pipeline already`}
                </summary>
                <ul>
                  {split.onBoard.slice(0, 40).map(r => (
                    <li key={`b${r.line}`}>
                      <b>{r.company || r.email}</b><span>{r.email}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </Field>
          ) : null}

          {/* The one judgement this screen cannot make for anybody. */}
          {split.noEmail.length ? (
            <Field label="" wide>
              <label className="imp-blank">
                <input
                  type="checkbox"
                  checked={takeBlank}
                  onChange={e => setTakeBlank(e.target.checked)}
                />
                <span>
                  <b>{`Bring in the ${split.noEmail.length} rows with no email address`}</b>
                  <small>
                    They cannot be checked against Clients, so one of them may
                    be somebody who has already registered.
                  </small>
                </span>
              </label>
            </Field>
          ) : null}

          <Field label="" wide>
            <p className="imp-note">
              <Icon name="tag" />
              {toContacts
                ? 'Everything imported is marked Imported. Put one on the pipeline with the ⚡ button on its row, and the board will say it came in as a Contact.'
                : 'Everything imported is marked Imported — filter the board by source to find it again.'}
            </p>
          </Field>
        </>
      ) : null}
    </Sheet>
  );
}
